'use client';

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { hubButtonStyle } from '@/components/hub/hub-ui';
import { contrastRatio, rgba, toRgb } from '@/lib/branding/colour';
import { GROUNDS, MAX_SHIFT, AA, labelOn, mixHex } from '@/components/hub/ticket/organiser-skin';
import { MIN_LARGE, MIN_TEXT } from '@/lib/public/theme-colours';

/**
 * The race photo flow, in the v8 look everywhere it is drawn.
 *
 *  - 'page': the organiser's own photo pages (/photos/...), inside
 *    ThemedEventPage. The flow sits on the organiser's page colour, so
 *    PhotoPageSkin works the v8 tokens out against that ground, their text
 *    colours and their accent (photoPageSkin below), and the type is the
 *    event page's own: its body face, and its heading face, weight and
 *    colour. The banner and a centred column are the only layout differences.
 *  - 'hub': inside the athlete hub's race ticket, on the hub's own tokens
 *    (and so its theme and the HYROX skin). No banner: the ticket already
 *    names the race.
 *
 * Both draw the same parts, from here, so the two cannot drift apart.
 */
export type PhotoVariant = 'page' | 'hub';

export function StepHeading({ children, style }: { variant?: PhotoVariant; children: ReactNode; style?: CSSProperties }) {
  return (
    // The heading colour only exists on a photo page (PhotoPageSkin); the hub
    // falls through to its ink.
    <h2 style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--fv-photo-heading, var(--fvd-ink))', margin: '0 0 4px', lineHeight: 1.35, ...style }}>
      {children}
    </h2>
  );
}

export function StepText({ children, style }: { variant?: PhotoVariant; children: ReactNode; style?: CSSProperties }) {
  return <p style={{ fontSize: 13, color: 'var(--fvd-ink-secondary)', lineHeight: 1.55, margin: '0 0 16px', ...style }}>{children}</p>;
}

export function StepError({ children }: { variant?: PhotoVariant; children: ReactNode }) {
  return (
    <div
      role="alert"
      style={{ padding: '10px 12px', background: 'var(--fvd-danger-tint)', borderRadius: 'var(--fv-radius-tile, 8px)', color: 'var(--fvd-danger)', fontSize: 13, lineHeight: 1.5, marginBottom: 14, textAlign: 'left' }}
    >
      {children}
    </div>
  );
}

/** A quiet panel for "this is happening" notes (a zip starting, the share sheet refused). */
export function StepNote({ children, icon, role = 'status' }: { children: ReactNode; icon?: ReactNode; role?: 'status' | 'note' }) {
  return (
    <div
      role={role}
      style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', marginBottom: 14, borderRadius: 'var(--fv-radius-tile, 8px)', background: 'var(--fvd-neutral-chip)', fontSize: 13, color: 'var(--fvd-ink-secondary)', lineHeight: 1.5 }}
    >
      {icon && <span aria-hidden="true" style={{ flex: 'none', display: 'flex', marginTop: 2, color: 'var(--fvd-ink-muted)' }}>{icon}</span>}
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  );
}

/** The one filled button of a step. */
export function stepPrimaryStyle(_variant: PhotoVariant, opts: { isMobile: boolean; disabled?: boolean; themeColors?: unknown; fullWidth?: boolean }): CSSProperties {
  return hubButtonStyle('primary', opts.isMobile, {
    borderRadius: 'var(--fv-radius-button, 10px)',
    width: opts.fullWidth === false ? undefined : '100%',
    opacity: opts.disabled ? 0.5 : 1,
    cursor: opts.disabled ? 'default' : 'pointer',
  });
}

/** A quiet text action under a step ("I pre-purchased my photos"). */
export function stepLinkStyle(_variant: PhotoVariant, extra?: CSSProperties): CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 44, padding: 0, marginTop: 6,
    background: 'none', border: 'none', fontFamily: 'inherit',
    color: 'var(--fvd-teal-text)', fontSize: 12.5, fontWeight: 500, cursor: 'pointer', textAlign: 'left',
    textDecoration: 'none',
    ...extra,
  };
}

export function stepInputStyle(_variant: PhotoVariant, isMobile: boolean, extra?: CSSProperties): CSSProperties {
  return {
    width: '100%', minHeight: 44, padding: '10px 12px', lineHeight: 1.5, boxSizing: 'border-box',
    borderRadius: 'var(--fv-radius-control, var(--fvd-radius))', border: '1px solid var(--fvd-border-control)',
    background: 'transparent', color: 'var(--fvd-ink)', fontFamily: 'inherit',
    // 16px on a phone so iOS does not zoom.
    fontSize: isMobile ? 16 : 14,
    ...extra,
  };
}

/** The outline button ("Try another selfie", "Select all"). */
export function stepSecondaryStyle(_variant: PhotoVariant, isMobile: boolean, extra?: CSSProperties): CSSProperties {
  return hubButtonStyle('small', isMobile, { borderRadius: 'var(--fv-radius-button, 8px)', ...extra });
}

/** Small round spinner for "searching", drawn in the step's own ink. */
export const StepSpinner: (props: { variant?: PhotoVariant }) => ReactNode = () => {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'block', width: 22, height: 22, borderRadius: 99, margin: '0 0 12px',
        border: '2px solid var(--fvd-hairline-strong)', borderTopColor: 'var(--fvd-teal)',
        animation: 'spin 1s linear infinite',
      }}
    />
  );
};

/* ------------------------------------------------------------------ */
/* The public pages: v8 on the organiser's ground                      */
/* ------------------------------------------------------------------ */

export interface PageThemeColours {
  primary: string;
  secondary: string;
  text?: string;
  textMuted?: string;
  buttonText?: string;
  /** Headings; absent means the same as the text, as on the event page. */
  heading?: string;
}

const hex2 = (n: number) => Math.round(Math.max(0, Math.min(255, n))).toString(16).padStart(2, '0');

/** `#rgb` or `#rrggbb` to `#rrggbb`, or null. */
function normalHex(value: string | undefined | null): string | null {
  if (!value) return null;
  const rgb = toRgb(value.trim());
  return rgb ? `#${rgb.map(hex2).join('')}` : null;
}

/** The lightest blend of `ink` into `ground`, from `start`, that reads at AA on it. */
function inkOn(ground: string, ink: string, start: number): string {
  for (let t = start; t < 1; t += 0.04) {
    const c = mixHex(ground, ink, t);
    if (contrastRatio(c, ground) >= AA) return c;
  }
  return ink;
}

/**
 * The accent as text on this ground: itself where it reads at AA on the ground
 * and on its own tint, else pushed towards the ink by the least that does, and
 * past MAX_SHIFT (it would stop reading as the brand) the ink itself. Yellow on
 * a white page comes out as ink; yellow on black stays yellow.
 */
export function accentTextOnGround(accent: string, ground: string, ink: string): string {
  const tint = mixHex(ground, accent, 0.12);
  const passes = (c: string) => contrastRatio(c, ground) >= AA && contrastRatio(c, tint) >= AA;
  if (passes(accent)) return accent;
  const toward = contrastRatio('#000000', ground) >= contrastRatio('#FFFFFF', ground) ? '#000000' : '#FFFFFF';
  for (let step = 1; step * 0.05 <= MAX_SHIFT + 1e-9; step++) {
    const c = mixHex(accent, toward, step * 0.05);
    if (passes(c)) return c;
  }
  return ink;
}

export interface PhotoPageSkin {
  /** Which half of v8 the ground is closer to. */
  mode: 'light' | 'dark';
  vars: Record<string, string>;
}

/**
 * The v8 tokens for a photo page, worked out against the organiser's own page
 * colour rather than the viewer's theme: the page is the organiser's, and its
 * ground does not change with a phone's dark mode.
 *
 *   - Ink: the organiser's text colour, wherever the event page would use it
 *     (it reads on this ground at the event page's bar), else the v8 ink for
 *     whichever half the ground is nearer. The secondary ink is the lightest
 *     blend of that ink that still reads at AA; the muted ink is the
 *     organiser's muted colour where it reads at AA, else the same kind of
 *     blend (a mid-tone page would otherwise lose them).
 *   - Headings (--fv-photo-heading): the organiser's heading colour, or their
 *     text colour, where it reads at the large-text bar, as on the event page.
 *   - Fills (the one primary button, the selected tick): the organiser's
 *     accent, labelled in near-black or white, whichever reads.
 *   - Text in the accent (links, the selected age choice): accentTextOnGround.
 *   - Lines and outlines in the accent (the spinner, a selected border): the
 *     accent where it shows at 3:1 on the ground, else the text-safe one.
 *
 * The --fv-* names at the end are for the shared bits inside the flow that
 * still read the old tokens (EmailTypoHint, PaymentMethodsRow, focus rings
 * ThemedEventPage draws). Undefined when the ground is not a colour: the flow
 * then falls back to the v8 tokens of the viewer's theme.
 */
export function photoPageSkin(colours: PageThemeColours | null | undefined): PhotoPageSkin | undefined {
  const ground = normalHex(colours?.primary);
  if (!ground) return undefined;
  const mode: 'light' | 'dark' = contrastRatio(GROUNDS.light.ink, ground) >= contrastRatio(GROUNDS.dark.ink, ground) ? 'light' : 'dark';
  // The organiser's text where the event page would keep it; otherwise the v8
  // ink, unless a mid-tone ground needs the full black or white to read.
  const text = normalHex(colours?.text);
  const v8Ink = contrastRatio(GROUNDS[mode].ink, ground) >= AA
    ? GROUNDS[mode].ink
    : contrastRatio('#000000', ground) >= contrastRatio('#FFFFFF', ground) ? '#000000' : '#FFFFFF';
  const ink = text && contrastRatio(text, ground) >= MIN_TEXT ? text : v8Ink;
  const headingPick = normalHex(colours?.heading) ?? text;
  const heading = headingPick && contrastRatio(headingPick, ground) >= MIN_LARGE ? headingPick : ink;
  const mutedPick = normalHex(colours?.textMuted);
  const inkMuted = mutedPick && contrastRatio(mutedPick, ground) >= AA ? mutedPick : inkOn(ground, ink, 0.64);
  const accent = normalHex(colours?.secondary) ?? (mode === 'light' ? '#006D6D' : '#2ADFDF');
  const accentText = accentTextOnGround(accent, ground, ink);
  const accentLine = contrastRatio(accent, ground) >= 3 ? accent : accentText;
  const on = labelOn(accent);
  const inkA = (a: number) => rgba(ink, a) as string;
  const danger = mode === 'light' ? '#A33A22' : '#E4674A';
  const amberText = mode === 'light' ? '#9A6412' : '#E8BE73';
  const vars: Record<string, string> = {
    '--fvd-canvas': ground,
    '--fvd-elevated': mode === 'light' ? '#FFFFFF' : mixHex(ground, '#FFFFFF', 0.07),
    '--fvd-elevated-hairline': inkA(mode === 'light' ? 0.09 : 0.16),
    '--fvd-row-hover': mixHex(ground, ink, 0.03),
    '--fvd-neutral-chip': mixHex(ground, ink, mode === 'light' ? 0.05 : 0.08),
    '--fvd-selected-fill': inkA(0.09),
    '--fvd-ink': ink,
    '--fvd-ink-secondary': inkOn(ground, ink, 0.74),
    '--fvd-ink-muted': inkMuted,
    '--fvd-ink-disabled': mixHex(ground, ink, 0.42),
    '--fvd-hairline-strong': inkA(mode === 'light' ? 0.12 : 0.16),
    '--fvd-hairline': inkA(mode === 'light' ? 0.09 : 0.11),
    '--fvd-hairline-light': inkA(mode === 'light' ? 0.07 : 0.08),
    '--fvd-border-control': inkA(mode === 'light' ? 0.16 : 0.2),
    '--fvd-hover-tint': inkA(0.05),
    '--fvd-teal': accentLine,
    '--fvd-teal-hover': mixHex(accent, on, 0.12),
    '--fvd-teal-on': on,
    '--fvd-teal-solid': accent,
    '--fvd-teal-solid-on': on,
    '--fvd-teal-text': accentText,
    '--fvd-teal-tint': rgba(accent, 0.12) as string,
    '--fvd-teal-tint-pale': rgba(accent, 0.06) as string,
    '--fvd-danger': danger,
    '--fvd-danger-tint': rgba(danger, 0.12) as string,
    '--fvd-amber-text': amberText,
    '--fvd-amber-tint': rgba(mode === 'light' ? '#C08A2E' : '#D9A648', 0.15) as string,
    '--fv-accent': accentText,
    '--fv-accent-text': labelOn(accentText),
    '--fv-text-primary': ink,
    '--fv-text-secondary': inkOn(ground, ink, 0.74),
    '--fv-border': inkA(mode === 'light' ? 0.09 : 0.11),
    '--fv-bg-input': ground,
    '--button-text': on,
    '--fv-photo-heading': heading,
  };
  return { mode, vars };
}

/*
 * The type is the event page's. ThemedEventPage sets every h1-h3 in the
 * organiser's heading face at their weight (with !important), and the body
 * face on its wrapper, which the flow inherits. The old dashboard font
 * variables some shared parts still name are reset to the initial value, so a
 * font-family that reads them is dropped and the part inherits the page's face
 * instead of Instrument Sans. Written in a <style> rather than globals.css,
 * where Tailwind v4 has been seen dropping hand-written rules.
 */
const SKIN_CSS = `
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.fv-photo-skin {
  --font-instrument-sans: initial;
  --font-barlow-condensed: initial;
  --fv-font-display: initial;
}
`;

/**
 * The v8 photo flow on an organiser's themed page. Wraps everything the page
 * renders between ThemedEventPage's header and footer.
 */
export function PhotoPageSkin({ colours, children }: { colours: PageThemeColours; children: ReactNode }) {
  const skin = photoPageSkin(colours);
  return (
    <div
      // `dark` as a class too, so the few Tailwind dark: variants inside (the
      // card form's wallet row) follow the ground rather than the viewer.
      className={`fv-photo-skin${skin?.mode === 'dark' ? ' dark' : ''}`}
      // No font here: the body face comes from ThemedEventPage's wrapper.
      style={{
        ...(skin?.vars ?? {}),
        colorScheme: skin?.mode,
        color: 'var(--fvd-ink)',
      } as CSSProperties}
    >
      <style dangerouslySetInnerHTML={{ __html: SKIN_CSS }} />
      {children}
    </div>
  );
}

/** The column every public photo page sets its content in. */
export const PAGE_COLUMN: CSSProperties = { maxWidth: 560, margin: '0 auto', padding: '24px 16px 8px', boxSizing: 'border-box' };

/** "Not available yet": an unpublished album or event, said plainly. */
export function PhotosUnavailable({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div style={{ ...PAGE_COLUMN, padding: '56px 16px 24px', minHeight: '50vh' }}>
      <p style={{ margin: 0, fontSize: 13, fontWeight: 500, color: 'var(--fvd-ink-muted)' }}>Race photos</p>
      <h1 style={{ margin: '6px 0 0', fontSize: 24, fontWeight: 600, letterSpacing: '-0.028em', lineHeight: 1.2, color: 'var(--fv-photo-heading, var(--fvd-ink))' }}>
        {title || 'Photos are being processed'}
      </h1>
      <p style={{ margin: '8px 0 0', fontSize: 13.5, lineHeight: 1.55, color: 'var(--fvd-ink-secondary)', maxWidth: '46ch' }}>{children}</p>
    </div>
  );
}

/** A quiet text link in the accent, 44px tall to tap. */
export function PhotoTextLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} style={{ display: 'inline-flex', alignItems: 'center', minHeight: 44, fontSize: 12.5, fontWeight: 500, color: 'var(--fvd-teal-text)', textDecoration: 'none' }}>
      {children}
    </a>
  );
}

/* ------------------------------------------------------------------ */
/* The photo viewer                                                    */
/* ------------------------------------------------------------------ */

/** Round control over a photo: 44px to tap, the viewer's ink on a soft disc. */
export const viewerControlStyle = (size = 44): CSSProperties => ({
  width: size, height: size, flex: 'none', borderRadius: 'var(--fv-radius-button, 99px)', border: 'none', padding: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  background: 'var(--pv-chip, rgba(255,255,255,0.12))', color: 'var(--pv-ink, #FFFFFF)',
});

/**
 * The viewer's ground and ink. On an event page the ground is the page's own
 * background (--fv-page-bg, set by ThemedEventPage); elsewhere near-black. The
 * ink is black or white, whichever reads on that ground.
 */
function viewerColours(): { ground: string; ink: string } {
  const fallback = { ground: '#0B0A09', ink: '#FFFFFF' };
  if (typeof window === 'undefined') return fallback;
  const raw = getComputedStyle(document.body).getPropertyValue('--fv-page-bg').trim();
  if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(raw)) return fallback;
  const ink = contrastRatio('#000000', raw) >= contrastRatio('#FFFFFF', raw) ? '#000000' : '#FFFFFF';
  return { ground: raw, ink };
}

/** The viewer's background and the ink variables its controls read. Call once when it opens. */
export function viewerSurfaceStyle(): CSSProperties {
  const { ground, ink } = viewerColours();
  return {
    background: ground,
    ['--pv-ink' as string]: ink,
    ['--pv-ink-soft' as string]: `color-mix(in srgb, ${ink} 72%, transparent)`,
    ['--pv-chip' as string]: `color-mix(in srgb, ${ink} 12%, transparent)`,
    ['--pv-line' as string]: `color-mix(in srgb, ${ink} 32%, transparent)`,
  } as CSSProperties;
}

/**
 * Full-screen viewer for one photo at a time, on the event page's own
 * background (near-black anywhere else); the one action in the footer (if
 * any) takes the accent fill. Escape closes it, the arrow keys step
 * through, the page behind does not scroll, and focus goes to the close button
 * and back to where it was on close.
 */
export function PhotoViewer({
  label,
  index,
  count,
  onClose,
  onMove,
  children,
  footer,
}: {
  label: string;
  index: number;
  count: number;
  onClose: () => void;
  onMove: (next: number) => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const [surface] = useState(viewerSurfaceStyle);
  // The key handler is bound once; these keep it reading the latest props.
  const moveRef = useRef(onMove);
  const closeFn = useRef(onClose);
  const at = useRef({ index, count });
  useEffect(() => {
    moveRef.current = onMove;
    closeFn.current = onClose;
    at.current = { index, count };
  });

  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      const { index: i, count: n } = at.current;
      if (e.key === 'Escape') closeFn.current();
      else if (e.key === 'ArrowLeft' && i > 0) moveRef.current(i - 1);
      else if (e.key === 'ArrowRight' && i < n - 1) moveRef.current(i + 1);
    };
    window.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      before?.focus?.();
    };
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={label}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 60, ...surface,
        // The page's own face: the event's on a photo page, v8 in the hub.
        display: 'flex', flexDirection: 'column', fontFamily: 'inherit',
        paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', flex: 'none' }}>
        <span style={{ fontSize: 13, color: 'var(--pv-ink-soft, rgba(255,255,255,0.72))', fontFamily: 'var(--fvd-font-mono)', fontVariantNumeric: 'tabular-nums' }}>
          {index + 1} of {count}
        </span>
        <button ref={closeRef} type="button" aria-label="Close" onClick={(e) => { e.stopPropagation(); onClose(); }} style={viewerControlStyle()}>
          <X size={20} />
        </button>
      </div>
      <div onClick={(e) => e.stopPropagation()} style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '0 8px' }}>
        {children}
      </div>
      <div onClick={(e) => e.stopPropagation()} style={{ flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 12 }}>
        <button
          type="button"
          aria-label="Previous photo"
          disabled={index === 0}
          onClick={() => onMove(index - 1)}
          style={{ ...viewerControlStyle(48), opacity: index === 0 ? 0.35 : 1, cursor: index === 0 ? 'default' : 'pointer' }}
        >
          <ChevronLeft size={20} />
        </button>
        {footer ?? <span style={{ flex: 1, maxWidth: 320 }} />}
        <button
          type="button"
          aria-label="Next photo"
          disabled={index >= count - 1}
          onClick={() => onMove(index + 1)}
          style={{ ...viewerControlStyle(48), opacity: index >= count - 1 ? 0.35 : 1, cursor: index >= count - 1 ? 'default' : 'pointer' }}
        >
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
}

/** The footer action in a viewer: "This is me", in the accent fill once chosen. */
export function viewerActionStyle(on: boolean): CSSProperties {
  return {
    flex: 1, maxWidth: 320, minHeight: 48, padding: '0 16px', borderRadius: 'var(--fv-radius-button, 10px)',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    fontFamily: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
    background: on ? 'var(--fvd-teal-solid)' : 'var(--pv-chip, rgba(255,255,255,0.12))',
    color: on ? 'var(--fvd-teal-solid-on)' : 'var(--pv-ink, #FFFFFF)',
    border: on ? 'none' : '1px solid var(--pv-line, rgba(255,255,255,0.32))',
  };
}

/** The tick on a photo tile: a 24px mark inside a 40px tap area. */
export function TileTick({ on, children }: { on: boolean; children?: ReactNode }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 24, height: 24, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: on ? 'var(--fvd-teal-solid)' : 'rgba(0,0,0,0.45)',
        color: on ? 'var(--fvd-teal-solid-on)' : '#FFFFFF',
        border: on ? 'none' : '1.5px solid rgba(255,255,255,0.75)',
        boxSizing: 'border-box',
      }}
    >
      {on ? children : null}
    </span>
  );
}
